import React, { useEffect, useMemo } from 'react';
import { useGate, useStore } from 'effector-react';
import {
  type LibItemMatrix,
  type LibItemPano,
  type LibItemPVideo,
  type LibItemScene,
  LibType,
} from '@types';
import Title from '@components/Title';
import Button from '@components/Button';
import LibModal from '../layouts/LibModal';
import LibModalNav from '../layouts/LibModalNav';
import LibModalSpace from '../layouts/LibModalSpace';
import { TipMatrix, TipPano, TipPVideo } from './libSceneTips';
import SceneListPano from './SceneListPano';
import SearchBar from '../components/SearchBar';
import FooterClear from '../components/FooterClear';
import {
  LibAddedSetGate,
  LibItemsGate,
  LibMultipleGate,
} from '../stores/libState';
import { $libSelected } from '../stores/libSelected';
import SceneListMatrix from './SceneListMatrix';
import onAlbumClick from '../actions/onAlbumClick';
import SceneListPVideo from './SceneListPVideo';
import useSocket from '../utils/useSocket';
import UploadButtonPano from './UploadButtonPano';
import UploadButtonMatrix from './UploadButtonMatrix';
import UploadButtonPVideo from './UploadButtonPVideo';
import libReset from '../actions/libReset';
import { useLibRecent } from '../utils/libRecent';

type NavType = LibType.pano | LibType.panoVideo | LibType.matrix;
const names: Record<NavType, string> = {
  [LibType.pano]: '全景图片',
  [LibType.panoVideo]: '全景视频',
  [LibType.matrix]: '高清矩阵',
};

type Props = {
  nav: NavType[];
  multiple?: boolean;
  disableUpload?: boolean;
  addedIds?: Partial<Record<NavType, Set<number>>>;
  onConfirm: (result: {
    panos?: LibItemPano[];
    panoVideos?: LibItemPVideo[];
    matrices?: LibItemMatrix[];
  }) => void;
  onRequestClose: () => void;
};

function LibSceneModal({
  nav,
  multiple = false,
  disableUpload = false,
  addedIds = {},
  onConfirm,
  onRequestClose,
}: Props) {
  useSocket();
  const [curNav, setCurNav] = useLibRecent(
    'LibSceneModal',
    nav[0],
    (lastNav) => !nav.includes(lastNav!)
  );
  const navItems = useMemo(
    () => nav.map((x) => ({ id: x, name: names[x] })),
    [nav]
  );

  const noSelected = !useStore($libSelected).length;

  useGate(LibMultipleGate, multiple);
  useGate(LibAddedSetGate, addedIds);
  useEffect(() => libReset, []);

  const handleNavChange = (navType: NavType) => {
    onAlbumClick(0);
    setCurNav(navType);
  };

  const handleConfirm = () => {
    const selected = $libSelected.getState();
    const items = (LibItemsGate.state.getState() as LibItemScene[])
      .filter((x) => selected.includes(x.id))
      .sort((a, b) => selected.indexOf(a.id) - selected.indexOf(b.id));
    if (curNav === LibType.pano) {
      onConfirm({ panos: items as LibItemPano[] });
    } else if (curNav === LibType.panoVideo) {
      onConfirm({ panoVideos: items as LibItemPVideo[] });
    } else if (curNav === LibType.matrix) {
      onConfirm({ matrices: items as LibItemMatrix[] });
    }
  };

  const renderButton = () => {
    if (disableUpload) return null;
    if (curNav === LibType.pano) return <UploadButtonPano />;
    if (curNav === LibType.panoVideo) return <UploadButtonPVideo />;
    if (curNav === LibType.matrix) return <UploadButtonMatrix />;
  };

  return (
    <LibModal
      header={
        <>
          <Title>作品素材库</Title>
          <SearchBar />
          {renderButton()}
        </>
      }
      footer={
        <>
          <Button disabled={noSelected} onClick={handleConfirm}>
            确认操作
          </Button>
          <FooterClear />
        </>
      }
      onRequestClose={onRequestClose}
    >
      <LibModalNav
        navItems={navItems}
        selected={curNav}
        onChange={handleNavChange}
      >
        <LibModalSpace />
        {curNav === LibType.pano && <TipPano />}
        {curNav === LibType.panoVideo && <TipPVideo />}
        {curNav === LibType.matrix && <TipMatrix />}
      </LibModalNav>
      {curNav === LibType.pano && <SceneListPano />}
      {curNav === LibType.panoVideo && <SceneListPVideo />}
      {curNav === LibType.matrix && <SceneListMatrix />}
    </LibModal>
  );
}

export default LibSceneModal;
